import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
} from 'rxjs';
import { NgCommService } from 'src/app/ajs-ng-communication.service';
import { AjsStateService } from 'src/app/ajs-state.service';
import { states } from 'src/app/core/application.states';
import { MapRequester } from 'src/app/requesters/map/map.requester';

import {
	IMapActionModalData,
	MapAction,
	MapActionPopupComponent,
} from '../components/map-action-popup/map-action-popup.component';
import { MapAdapter } from '../map.adapter';
import {
	bulkDeleteMaps,
	deleteMap,
	deleteMapFail,
	deleteMapSuccess,
	duplicateMap,
	duplicateMapFail,
	duplicateMapSuccess,
	fetchMapListFail,
	fetchMapListSuccess,
	initMapList,
	navigateToMap,
	navigateToMapCreate,
	openDeleteConfirmationPopup,
	refreshMapList,
	toggleMapStatus,
} from './map.actions';

@Injectable()
export class MapEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToMapCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToMapCreate),
				tap(() => this.stateService.go(states.MapCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToMap = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToMap),
				tap((a) =>
					a.goToVisuTab
						? this.stateService.go(states.MapVisualization, {
								id: `${a.id}`,
								mode: 'visualisation',
						  })
						: this.stateService.go(states.MapEdit, {
								id: `${a.id}`,
						  })
				)
			),
		{
			dispatch: false,
		}
	);
	// ##########################################################
	// Business Effects
	// ##########################################################

	public initMapList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initMapList),
			switchMap(() => this.mapRequester.getMapList()),
			map((response) =>
				fetchMapListSuccess({
					mapList: this.mapAdapter.generateMapList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchMapListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshMapList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshMapList),
			switchMap(() => this.mapRequester.getMapList(false)),
			map((response) =>
				fetchMapListSuccess({
					mapList: this.mapAdapter.generateMapList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchMapListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleMapStatus),
			switchMap((action) => this.mapRequester.getMap(action.mapId)),
			switchMap((map) =>
				this.mapRequester.changeActivationStatusMap({
					...map,
					metadata: {
						...map.metadata,
						actif: !map.metadata.actif,
					},
				})
			),
			map(() => refreshMapList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshMapList());
				return caught;
			})
		)
	);

	public deleteMap$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteMap),
			switchMap((action) => this.mapRequester.deleteMap(action.mapId)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@map.delete.success:`
				);
				this.store.dispatch(deleteMapSuccess());
				return refreshMapList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError($localize`:i18n=@@map.delete.error:`);
				this.store.dispatch(
					deleteMapFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public bulkDeleteMaps$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteMaps),
			switchMap((action) => this.mapRequester.deleteManyMaps(action.mapIds)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@map.delete.multiple.success:`
				);
				this.store.dispatch(deleteMapSuccess());
				return refreshMapList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@map.delete.multiple.error:`
				);
				this.store.dispatch(
					deleteMapFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public duplicateMap$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateMap),
			switchMap((action) => this.mapRequester.duplicateMap(action.mapId)),
			map((id) => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@map.duplicate.success:`
				);
				return duplicateMapSuccess({
					id,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@map.duplicate.error:`
				);
				this.store.dispatch(duplicateMapFail(error));
				return caught;
			})
		)
	);

	public navigateToMapEditAfterDuplicate$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateMapSuccess),
			map((action) =>
				navigateToMap({
					id: action.id,
					goToVisuTab: false,
				})
			)
		)
	);

	public deleteFailAction$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(deleteMapFail),
				tap(({ error }) => {
					const httpErrorsResponse = error as HttpErrorResponse;
					// eslint-disable-next-line camelcase
					const { delete_errors_report } = httpErrorsResponse.error;

					// eslint-disable-next-line camelcase
					if (
						delete_errors_report.total_deleted_element &&
						delete_errors_report.total_deleted_element !== 0
					) {
						this.store.dispatch(refreshMapList());
					}
				})
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IMapActionModalData>(
					MapActionPopupComponent,
					{
						title: $localize`:i18n=@@map.action.popup.delete.header:`,
						subTitle: '',
						mapId: action.mapId,
						element: action.mapLabel,
						action: MapAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.mapId.length === 1) {
					return deleteMap({
						mapId: action.mapId[0],
					});
				} else {
					return bulkDeleteMaps({
						mapIds: action.mapId,
					});
				}
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly mapRequester: MapRequester,
		private readonly mapAdapter: MapAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
